import React, { useCallback, useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { NotificationManager, prepareRequest } from '@atlas/core';
import { parseWithdrawalQuery } from '@atlas/routing';
import { Button, DefaultPopup, HeaderTitle, Input, Label, Loader, Select } from '@atlas/ui';

import { LocalConfig, transformParameters } from 'common/utils';
import PaymentMethodItem from 'src/components/ClientProfile/PaymentTab/PaymentMethodItem';

import { showWithdrawalError, submitWithdrawal } from './withdrawalUtils';

const accountNumberMaxLength = 18;
const clientNumberMaxLength = 18;

const countries = {
  Chile: 'CL',
  Mexico: 'MX',
};

const documentTypes = {
  Chile: 'RUT',
  Mexico: 'CURP',
};

const validateAccountNumber = (accountType, country, value) => {
  const chileAccountNumberMinLength = accountType === '0004' ? 7 : 8;
  const chileAccountNumberMaxLength = accountType === '0004' ? 8 : 16;
  const mexicoAccountNumberLength = accountType === '0010' ? 16 : 18;
  const isChile = country === countries.Chile;
  const minLength = isChile ? chileAccountNumberMinLength : mexicoAccountNumberLength;
  const maxLength = isChile ? chileAccountNumberMaxLength : mexicoAccountNumberLength;

  if (!value) return { message: 'required' };

  if (value.length < minLength) {
    return {
      message: 'min_length',
      params: {
        ruleOption: minLength,
      },
    };
  } else if (value.length > maxLength) {
    return {
      message: 'max_length',
      params: {
        ruleOption: maxLength,
      },
    };
  } else {
    return null;
  }
};

const validateClientNumber = (type, value) => {
  const clientNumberLength = type === documentTypes.Chile ? 9 : 18;

  if (!value) return { message: 'required' };

  if (value.length < clientNumberLength) {
    return {
      message: 'min_length',
      params: {
        ruleOption: clientNumberLength,
      },
    };
  } else if (value.length > clientNumberLength) {
    return {
      message: 'max_length',
      params: {
        ruleOption: clientNumberLength,
      },
    };
  } else {
    return null;
  }
};

const WithdrawalPayretailersPopup = ({ data, id }) => {
  const { paymentMethod, pmId, publicPaymentId, redirectUrl } = data;

  const [loading, setLoading] = useState(true);
  const [isActiveInput, setIsActiveInput] = useState(false);
  const [isActiveSelect, setIsActiveSelect] = useState(false);
  const [fieldsData, setFieldsData] = useState({});
  const [clientError, setClientError] = useState(null);
  const [accountError, setAccountError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const projectId = LocalConfig.get('projectId');

  const [accountNumberInput, setAccountNumberInput] = useState('');
  const [bankCodeInput, setBankCodeInput] = useState('');
  const [clientNumberInput, setClientNumberInput] = useState('');
  const [accountTypeInput, setAccountTypeInput] = useState('');

  const { amount, country, currency, documentType } = parseWithdrawalQuery(redirectUrl);

  const showClientNumberField = useMemo(
    () =>
      (documentType === documentTypes.Mexico && country === countries.Mexico) ||
      (documentType === documentTypes.Chile && country === countries.Chile),
    [documentType, country],
  );

  const handleClose = useCallback(() => {
    NotificationManager.removeNotification(id);
  }, [id]);

  const requestUrl = useMemo(
    () =>
      `payment/v2/payments/payretailers/parameters?project-id=${projectId}&country=${country}&param-names=BANK_CODE&param-names=ACCOUNT_TYPE`,
    [country, projectId],
  );

  useEffect(() => {
    const getFieldsData = prepareRequest({
      method: 'GET',
      url: requestUrl,
    });

    setLoading(true);
    getFieldsData()
      .then(response => {
        const parsedData = transformParameters(response.paymentParametersList);
        setFieldsData(parsedData);
      })
      .catch(() => {
        showWithdrawalError();
        handleClose();
      })
      .finally(() => {
        setLoading(false);
      });
  }, [handleClose, requestUrl]);

  const changeClientNumber = useCallback(
    event => {
      const value = event.target.value;

      const error = validateClientNumber(documentType, value);

      setClientError(error);
      setClientNumberInput(value);
    },
    [documentType],
  );

  const changeAccountNumber = useCallback(
    event => {
      const value = event.target.value.replace(/\D/g, '');

      const error = validateAccountNumber(accountTypeInput, country, value);

      setAccountError(error);
      setAccountNumberInput(value);
    },
    [accountTypeInput, country],
  );

  const changeAccountType = useCallback(
    value => {
      setAccountTypeInput(value);

      if (accountNumberInput.length) {
        const error = validateAccountNumber(value, country, accountNumberInput);
        setAccountError(error);
      }
    },
    [accountNumberInput, country],
  );

  const handleClickButton = useCallback(async () => {
    const requestData = {
      additionalParameters: {
        accountNumber: accountNumberInput,
        accountType: accountTypeInput,
        bankId: bankCodeInput,
        publicPaymentId: publicPaymentId,
        ...(showClientNumberField && {
          documentNumber: clientNumberInput,
          documentType,
        }),
      },
      amount: amount,
      currency: currency,
      pmId: pmId,
    };

    setIsSubmitting(true);

    try {
      await submitWithdrawal({ amount, currency, paymentMethod, requestData });
    } catch {
      showWithdrawalError();
    } finally {
      setIsSubmitting(false);
      handleClose();
    }
  }, [
    accountNumberInput,
    accountTypeInput,
    amount,
    bankCodeInput,
    clientNumberInput,
    currency,
    documentType,
    handleClose,
    paymentMethod,
    pmId,
    publicPaymentId,
    showClientNumberField,
  ]);

  return (
    <DefaultPopup
      className="payment-popup"
      headerTitle="withdrawal"
      headerTitlePosition="left"
      onClose={handleClose}
    >
      <HeaderTitle className="block-big payment-popup-title">
        <PaymentMethodItem amount={amount} paymentMethod={paymentMethod} showAmount />
      </HeaderTitle>

      {loading && (
        <Loader className={cx('form-loader', { 'loader-long': showClientNumberField })} />
      )}

      {!loading && (
        <>
          <div className="payment-popup-content">
            <div className="payment-popup-block">
              <Input
                active={isActiveInput === 'account_number'}
                className="popup-input"
                error={accountError}
                label="payretailers_withdrawal_account_number"
                maxLength={accountNumberMaxLength}
                onBlur={() => setIsActiveInput(false)}
                onChange={changeAccountNumber}
                onFocus={() => setIsActiveInput('account_number')}
                value={accountNumberInput}
              />
              {accountError && (
                <div className="help-text-container">
                  <Label message={accountError.message} params={accountError.params} />
                </div>
              )}
            </div>
            {fieldsData['ACCOUNT_TYPE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_account_type'}
                className="payment-popup-select"
                label="payretailers_withdrawal_account_type"
                onBlur={() => setIsActiveSelect(false)}
                onChange={changeAccountType}
                onFocus={() => setIsActiveSelect('withdrawal_account_type')}
                options={fieldsData['ACCOUNT_TYPE']}
              />
            )}
            {fieldsData['BANK_CODE'] && (
              <Select
                active={isActiveSelect === 'withdrawal_bank_code'}
                className="payment-popup-select"
                label="payretailers_withdrawal_bank_code"
                onBlur={() => setIsActiveSelect(false)}
                onChange={setBankCodeInput}
                onFocus={() => setIsActiveSelect('withdrawal_bank_code')}
                options={fieldsData['BANK_CODE']}
              />
            )}
            {showClientNumberField && (
              <div className="payment-popup-block">
                <Input
                  active={isActiveInput === 'client_number'}
                  className="popup-input"
                  error={clientError}
                  label={`${documentType.toLowerCase()}_withdrawal_client_number`}
                  maxLength={clientNumberMaxLength}
                  onBlur={() => setIsActiveInput(false)}
                  onChange={changeClientNumber}
                  onFocus={() => setIsActiveInput('client_number')}
                  value={clientNumberInput}
                />
                {clientError && (
                  <div className="help-text-container">
                    <Label message={clientError.message} params={clientError.params} />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="popup__footer">
            <Button
              className="primary big block"
              disabled={!accountNumberInput || isSubmitting || clientError || accountError}
              error={clientError || accountError}
              label="withdrawal"
              loading={isSubmitting}
              onClick={handleClickButton}
            />
          </div>
        </>
      )}
    </DefaultPopup>
  );
};

WithdrawalPayretailersPopup.propTypes = {
  data: PropTypes.shape({
    paymentMethod: PropTypes.string,
    pmId: PropTypes.string,
    publicPaymentId: PropTypes.string,
    redirectUrl: PropTypes.string,
  }).isRequired,
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default WithdrawalPayretailersPopup;
